import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { LoaderCircle } from 'lucide-react';
import { ProductCostOverTimeData } from '../../../utils/chartsUtils';
import { getColorPalette } from '../../../utils/chartColors';
import { ProductSelector } from './ProductSelector';
import { useChartTheme } from '../../../utils/chartTheme';

interface ProductCostOverTimeChartProps {
  data: ProductCostOverTimeData[];
  loading?: boolean;
}

export const ProductCostOverTimeChart: React.FC<ProductCostOverTimeChartProps> = ({ data, loading }) => {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Default top 5 items ranked by average cost % across periods that have data
  const defaultTopItems = useMemo(() => {
    const scored = data.map(item => {
      const nonZero = item.data.filter(d => d.costPercentage > 0);
      const avg = nonZero.length > 0
        ? nonZero.reduce((sum, d) => sum + d.costPercentage, 0) / nonZero.length
        : 0;
      return { name: item.name, avg };
    });
    scored.sort((a, b) => b.avg - a.avg);
    return scored.slice(0, 5).map(s => s.name);
  }, [data]);

  // Seed selection on first data, and re-seed when the time window changes such
  // that none of the currently selected items are present in the new data.
  useEffect(() => {
    if (data.length === 0) return;
    const currentNames = new Set(data.map(item => item.name));
    setSelectedItems(prev => {
      const hasOverlap = Array.from(prev).some(name => currentNames.has(name));
      if (prev.size === 0 || !hasOverlap) {
        return new Set(defaultTopItems);
      }
      return prev;
    });
  }, [data, defaultTopItems]);

  const colors = getColorPalette(data.length);
  const colorMap = useMemo(() => {
    const map = new Map<string, string>();
    data.forEach((item, index) => {
      map.set(item.name, colors[index]);
    });
    return map;
  }, [data, colors]);

  const chartData = useMemo(() => {
    if (data.length === 0) return [];

    // Every item has the same periods in chronological order (one push per period
    // in calculateProductCostPercentageOverTime), so take the order from the first
    // item. A string sort would misorder labels like "W10" before "W5".
    const orderedPeriods = data[0].data.map(d => d.period);

    return orderedPeriods.map(period => {
      const point: any = { period };
      data.forEach(item => {
        if (selectedItems.has(item.name)) {
          const periodData = item.data.find(d => d.period === period);
          point[item.name] = periodData?.costPercentage || 0;
        }
      });
      return point;
    });
  }, [data, selectedItems]);

  // Calculate dynamic Y-axis domain based on selected items
  const yAxisDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 100];

    // Get all cost percentage values from selected items
    const allValues: number[] = [];
    chartData.forEach(point => {
      data.forEach(item => {
        if (selectedItems.has(item.name) && point[item.name] !== undefined) {
          allValues.push(point[item.name]);
        }
      });
    });

    // Filter out zero values to get realistic min/max
    const nonZeroValues = allValues.filter(v => v > 0);

    if (nonZeroValues.length === 0) return [0, 100];

    const minValue = Math.min(...nonZeroValues);
    const maxValue = Math.max(...nonZeroValues);

    // Add 10% padding to min/max
    const range = maxValue - minValue;
    const padding = Math.max(range * 0.1, 5); // At least 5% padding

    const yMin = Math.max(0, Math.floor(minValue - padding));
    const yMax = Math.min(100, Math.ceil(maxValue + padding));

    return [yMin, yMax];
  }, [chartData, data, selectedItems]);

  const toggleItem = (itemName: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemName)) {
      newSelected.delete(itemName);
    } else {
      newSelected.add(itemName);
    }
    setSelectedItems(newSelected);
  };

  const getItemColor = (itemName: string) => {
    return colorMap.get(itemName) || '#53B1FD';
  };

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-card-dark p-4 rounded-[12px] shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoaderCircle className="h-8 w-8 animate-spin text-textPrimary dark:text-textPrimary-dark" />
          </div>
        ) : (
          <p className="text-[#475467] dark:text-textSecondary-dark text-center py-8">{t('charts.noData')}</p>
        )}
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className="rounded-lg bg-white dark:bg-background-dark pl-3 pr-[18px] py-3 shadow-lg border border-[#F5F5F5] dark:border-border-dark">
        <div className="flex flex-col gap-3">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-5">
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-[3px]"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-[#475467] dark:text-textTertiary-dark text-[12px] leading-[10px]">{entry.dataKey}</span>
              </div>
              <span className="tabular-nums text-[#101828] dark:text-textPrimary-dark text-[12px] leading-[10px] font-medium">
                {`: ${entry.value?.toFixed(1) ?? 0}%`}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const ChartContent = () => (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={chartData} margin={{ top: 5, right: 30, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.darkMode ? '#1E3A4E' : '#E5E7EB'} />
        <XAxis
          dataKey="period"
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.axisTick, fontSize: isMobile ? 10 : 12 }}
          dy={10}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.axisTick, fontSize: isMobile ? 10 : 12 }}
          domain={yAxisDomain}
          tickFormatter={(value: number) => `${value}%`}
        />
        <Tooltip content={<CustomTooltip />} />
        {/* Mobile: hide the in-chart legend (the external chip row below already lists the series
            and reclaims the fixed chart height). Desktop keeps it. */}
        {!isMobile && (
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
            formatter={(value) => (
              <span className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium">{value}</span>
            )}
          />
        )}
        {data
          .filter(item => selectedItems.has(item.name))
          .map((item, index) => (
            <Line
              key={item.name}
              type="monotone"
              dataKey={item.name}
              stroke={colorMap.get(item.name) || colors[index]}
              strokeWidth={isMobile ? 2 : 3}
              dot={false}
              activeDot={{ r: isMobile ? 4 : 6 }}
            />
          ))}
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <div className="bg-white dark:bg-card-dark p-4 rounded-[12px] relative shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
      <div className='mb-4 sm:mb-8 flex items-center justify-between w-full'>
        <div>
          <h1 className='text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1'>{t('charts.productCostOverTimeTitle')}</h1>
          <p className='text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal'>{t('charts.productCostOverTimeDescription')}</p>
        </div>
        <button
          className='border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 pl-2 pr-3 hidden md:flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300'
          onClick={() => setIsOpen(true)}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
            <path d="M5.83317 14.1667H8.33317C8.56928 14.1667 8.7672 14.2466 8.92692 14.4063C9.08664 14.5661 9.1665 14.764 9.1665 15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334H4.99984C4.76373 15.8334 4.56581 15.7536 4.40609 15.5938C4.24637 15.4341 4.1665 15.2362 4.1665 15.0001V11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334C5.23595 10.8334 5.43387 10.9133 5.59359 11.073C5.75331 11.2327 5.83317 11.4306 5.83317 11.6667V14.1667ZM14.1665 5.83341H11.6665C11.4304 5.83341 11.2325 5.75355 11.0728 5.59383C10.913 5.43411 10.8332 5.23619 10.8332 5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675H14.9998C15.2359 4.16675 15.4339 4.24661 15.5936 4.40633C15.7533 4.56605 15.8332 4.76397 15.8332 5.00008V8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675C14.7637 9.16675 14.5658 9.08689 14.4061 8.92717C14.2464 8.76744 14.1665 8.56953 14.1665 8.33342V5.83341Z" fill="currentColor" />
          </svg>
          <span className='text-[#667085] dark:text-textQuaternary-dark text-[14px] leading-5 font-semibold'>Expand</span>
        </button>
      </div>

      <div className="h-[300px] w-full">
        <ChartContent />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2.5">
        {data
          .filter(item => selectedItems.has(item.name))
          .map((item, index) => (
            <div
              key={item.name}
              className="flex items-center gap-1.5 cursor-pointer group hover:bg-gray-50 dark:hover:bg-white/5 px-2 py-1 rounded-lg transition-colors"
              onClick={() => toggleItem(item.name)}
            >
              <span
                className="inline-block h-2.5 w-2.5 rounded-[3px] flex-shrink-0"
                style={{ backgroundColor: colorMap.get(item.name) || colors[index] }}
              />
              <span className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium group-hover:text-[#101828] dark:group-hover:text-textPrimary-dark">
                {item.name}
              </span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="opacity-0 group-hover:opacity-100 transition-opacity ml-1"
              >
                <path
                  d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                  stroke={theme.darkMode ? '#9CA3AF' : '#667085'}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          ))}
        <ProductSelector
          allItems={data}
          selectedItems={selectedItems}
          onToggleItem={toggleItem}
          getItemColor={getItemColor}
          placeholder="Search products to add..."
        />
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#16192466] dark:bg-[#16192466]" onClick={() => setIsOpen(false)}>
          <div className="bg-white dark:bg-card-dark rounded-xl w-full max-w-[850px] p-5 relative" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h1 className='text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1'>{t('charts.productCostOverTimeTitle')}</h1>
                <p className='text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal'>{t('charts.productCostOverTimeDescription')}</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 pl-2 pr-3 flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
                  <path d="M7.49984 12.5001H4.99984C4.76373 12.5001 4.56581 12.4202 4.40609 12.2605C4.24637 12.1008 4.1665 11.9029 4.1665 11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334H8.33317C8.56928 10.8334 8.7672 10.9133 8.92692 11.073C9.08664 11.2327 9.1665 11.4306 9.1665 11.6667V15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334C8.09706 15.8334 7.89914 15.7536 7.73942 15.5938C7.5797 15.4341 7.49984 15.2362 7.49984 15.0001V12.5001ZM12.4998 7.50008H14.9998C15.2359 7.50008 15.4339 7.57994 15.5936 7.73966C15.7533 7.89939 15.8332 8.0973 15.8332 8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675H11.6665C11.4304 9.16675 11.2325 9.08689 11.0728 8.92717C10.913 8.76744 10.8332 8.56953 10.8332 8.33342V5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675C11.9026 4.16675 12.1005 4.24661 12.2603 4.40633C12.42 4.56605 12.4998 4.76397 12.4998 5.00008V7.50008Z" fill="currentColor" />
                </svg>
                <span className="text-[#667085] dark:text-textQuaternary-dark text-[14px] leading-5 font-semibold">Collapse</span>
              </button>
            </div>
            <div className="w-full h-[400px]">
              <ChartContent />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              {data
                .filter(item => selectedItems.has(item.name))
                .map((item, index) => (
                  <div
                    key={item.name}
                    className="flex items-center gap-1.5 cursor-pointer group hover:bg-gray-50 dark:hover:bg-white/5 px-2 py-1 rounded-lg transition-colors"
                    onClick={() => toggleItem(item.name)}
                  >
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-[3px] flex-shrink-0"
                      style={{ backgroundColor: colorMap.get(item.name) || colors[index] }}
                    />
                    <span className="text-[#475467] dark:text-textSecondary-dark text-[14px] leading-5 font-medium group-hover:text-[#101828] dark:group-hover:text-textPrimary-dark">
                      {item.name}
                    </span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                    >
                      <path
                        d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                        stroke={theme.darkMode ? '#9CA3AF' : '#667085'}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                ))}
              <ProductSelector
                allItems={data}
                selectedItems={selectedItems}
                onToggleItem={toggleItem}
                getItemColor={getItemColor}
                placeholder="Search products to add..."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
